// src/pages/admin/AdminBanners.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getBanners, addBanner, updateBanner, deleteBanner } from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2 } from "react-icons/fi";

export default function AdminBanners() {
  const [banners,   setBanners]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [image,     setImage]     = useState("");
  const [saving,    setSaving]    = useState(false);

  const { register, handleSubmit, reset } = useForm();

  const load = async () => {
    const b = await getBanners();
    setBanners(b);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setImage("");
    reset({ order: banners.length + 1 });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    if (!image) { toast.error("Please upload a banner image"); return; }
    setSaving(true);
    try {
      await addBanner({ image, order: parseInt(data.order) });
      toast.success("Banner added");
      setModalOpen(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await deleteBanner(id);
    toast.success("Banner deleted");
    setBanners((prev) => prev.filter((b) => b.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm sm:text-lg flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> Add Banner
        </button>
      </div>

      {loading ? <Spinner center /> : (
        banners.length === 0 ? (
          <div className="card p-12 text-center text-gray-500">
            <p className="text-4xl mb-3">🖼️</p>
            <p>No banners yet. Add one to show on the homepage.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {banners.map((banner) => (
              <div key={banner.id} className="relative group rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-white">
                {/* Banner image */}
                <div className="aspect-video w-full overflow-hidden bg-gray-100">
                  {banner.image
                    ? <img src={banner.image} alt={`Banner ${banner.order}`} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-300 text-3xl">🖼️</div>
                  }
                </div>

                {/* Order badge */}
                <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  #{banner.order}
                </div>

                {/* Delete button – shows on hover */}
                <button
                  onClick={() => handleDelete(banner.id)}
                  className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  title="Delete banner"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* Add Banner Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add Banner" size="md">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Display Order</label>
            <input
              {...register("order")}
              type="number"
              min="1"
              className="input"
              placeholder="1"
            />
          </div>
          <div>
            <label className="label">Banner Image *</label>
            <ImageUploader
              folder="banners"
              onUpload={(imgs) => setImage(imgs[0] || "")}
              existingImages={image ? [image] : []}
              maxFiles={1}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? <><Spinner size="sm" /> Saving...</> : "Add Banner"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}